import { ApiService, type ApiRequestConfig } from "./api.service";
import type { IWorkspaceMemberInvitation } from "@/types/workspace";

export class InvitationService extends ApiService {
  async fetchMyInvitations(config?: ApiRequestConfig): Promise<IWorkspaceMemberInvitation[]> {
    // Server-paginated (issue #1254); walk the pages so the pending-invites view
    // keeps the full set it renders locally. UI is unchanged.
    return this.getAllPages<IWorkspaceMemberInvitation>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (cursor) qs.set("cursor", cursor);
        return `/api/users/me/workspaces/invitations/?${qs}`;
      },
      undefined,
      config,
    );
  }

  async acceptInvitations(data: { invitations: string[] }): Promise<void> {
    return this.post(`/api/users/me/workspaces/invitations/`, data);
  }

  /** `setupToken` is the signed secret in a set-up user's emailed link (NG-3411); without it the
   *  response never says anything about set-up. */
  async getInvitationDetails(
    slug: string,
    id: string,
    setupToken?: string,
  ): Promise<IWorkspaceMemberInvitation> {
    const query = setupToken ? `?setup_token=${encodeURIComponent(setupToken)}` : "";
    return this.get(`/api/workspaces/${slug}/invitations/${id}/join/${query}`);
  }

  /** Public, single-use: only for a user Cognota set up (NG-3411). The password is sent once, never kept. */
  async setPasswordFromInvitation(
    slug: string,
    id: string,
    data: { email: string; password: string; token: string },
  ): Promise<{ success: true }> {
    return this.post(`/api/workspaces/${slug}/invitations/${id}/set-password/`, data);
  }

  async respondToInvitation(
    slug: string,
    id: string,
    data: { email: string; accepted: boolean },
  ): Promise<{ message: string }> {
    return this.post(`/api/workspaces/${slug}/invitations/${id}/join/`, data);
  }
}
